import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type LeagueLaunchCardContent,
} from "./types";

interface LeagueLaunchCardProps {
  format: CardFormat;
  content: LeagueLaunchCardContent;
}

const LeagueLaunchCard = forwardRef<HTMLDivElement, LeagueLaunchCardProps>(
  function LeagueLaunchCard({ format, content }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const themeId = content.theme ?? "collab";
    const theme = resolveCardTheme(themeId, {
      monoColor: content.monoColor,
      partnerColor: content.partnerColor,
    });

    const eyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: theme.fontEyebrow,
          fontSizePx: 24,
          color: theme.accentColor,
          letterSpacing: "0.4em",
          bold: true,
        },
        format,
      ),
      textTransform: "uppercase",
      textAlign: "center",
      marginTop: isStory ? 8 : 4,
    };

    const headlineDefault = isStory ? 180 : 140;
    const headlineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.headline,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: headlineDefault,
          color: theme.headlineColor,
          letterSpacing: "0.005em",
        },
        format,
      ),
      lineHeight: 0.92,
      textAlign: "center",
      marginTop: isStory ? 28 : 18,
      whiteSpace: "pre-wrap",
    };

    const goldStyle: CSSProperties = {
      ...resolveTextStyle(
        style.goldHighlight,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: headlineDefault,
          color: theme.accentColor,
        },
        format,
      ),
      display: "block",
      lineHeight: 0.92,
      textAlign: "center",
    };

    const taglineStyle: CSSProperties = {
      ...resolveTextStyle(
        style.tagline,
        {
          fontFamily: theme.fontBody,
          fontSizePx: isStory ? 30 : 24,
          color: theme.subtleTextColor,
          italic: true,
        },
        format,
      ),
      lineHeight: 1.4,
      textAlign: "center",
      marginTop: isStory ? 16 : 10,
    };

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("LEAGUE_LAUNCH")}
        footerRight={content.hashtags || "#FBKLeague · #FBK"}
        header={content.header}
        background={style.background}
        customBaseGradient={theme.customBaseGradient}
        headerOnLight={theme.headerOnLight}
        suppressBrandOverlays={theme.suppressBrandOverlays}
        hideFooter={content.footer?.hide}
      >
        <ThemeOverlay theme={theme} format={format} />

        {/* PARTNER HERO — big logo + "powered by FBK" */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            paddingTop: isStory ? 28 : 18,
            paddingBottom: isStory ? 20 : 14,
            gap: isStory ? 14 : 10,
          }}
        >
          {content.partnerLogoUrl ? (
            <img
              src={content.partnerLogoUrl}
              alt=""
              crossOrigin={
                content.partnerLogoUrl.startsWith("data:") ? undefined : "anonymous"
              }
              style={{
                height: isStory ? 200 : 140,
                width: "auto",
                maxWidth: isStory ? 720 : 560,
                objectFit: "contain",
                filter: "drop-shadow(0 16px 32px rgba(0,0,0,0.45))",
              }}
            />
          ) : (
            <div
              style={{
                fontFamily: theme.fontHeadline,
                fontSize: isStory ? 100 : 80,
                color: "#FFFFFF",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                textAlign: "center",
                lineHeight: 1,
              }}
            >
              {content.partnerName || "PARTNER"}
            </div>
          )}
          <div
            style={{
              fontFamily: theme.fontBody,
              fontSize: isStory ? 18 : 15,
              color: theme.subtleTextColor,
              letterSpacing: "0.3em",
              textTransform: "uppercase",
              opacity: 0.85,
              marginTop: 4,
            }}
          >
            powered by <span style={{ color: theme.accentColor, fontWeight: 800 }}>FBK</span>
          </div>
        </div>

        {/* CENTER content */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            position: "relative",
            zIndex: 2,
            minHeight: 0,
          }}
        >
          <div style={eyebrowStyle}>{content.eyebrow}</div>

          <div style={headlineStyle}>
            {content.headline}
            {content.goldHighlight && (
              <span style={goldStyle}>{content.goldHighlight}</span>
            )}
          </div>

          {content.tagline && <div style={taglineStyle}>{content.tagline}</div>}

          {/* PRIZE BLOCK */}
          {content.prizeText && (
            <div
              style={{
                marginTop: isStory ? 40 : 28,
                padding: isStory ? "28px 36px" : "20px 28px",
                borderRadius: 24,
                background: "rgba(0,0,0,0.32)",
                border: `2px solid ${theme.accentColor}`,
                textAlign: "center",
                boxShadow: `0 0 40px ${theme.accentColor}30`,
              }}
            >
              <div
                style={{
                  fontFamily: theme.fontEyebrow,
                  fontSize: isStory ? 22 : 18,
                  color: theme.accentColor,
                  letterSpacing: "0.4em",
                  textTransform: "uppercase",
                  fontWeight: 800,
                  marginBottom: isStory ? 14 : 10,
                }}
              >
                🏆 {content.prizeTitle}
              </div>
              <div
                style={{
                  fontFamily: theme.fontHeadline,
                  fontSize: isStory ? 56 : 42,
                  color: "#FFFFFF",
                  lineHeight: 1.1,
                  letterSpacing: "0.01em",
                  whiteSpace: "pre-wrap",
                }}
              >
                {content.prizeText}
              </div>
            </div>
          )}

          {/* QR + join code row */}
          {(content.qrImageUrl || content.joinCode) && (
            <div
              style={{
                marginTop: isStory ? 36 : 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: isStory ? 32 : 22,
              }}
            >
              {content.qrImageUrl && (
                <img
                  src={content.qrImageUrl}
                  alt=""
                  crossOrigin={
                    content.qrImageUrl.startsWith("data:") ? undefined : "anonymous"
                  }
                  style={{
                    width: isStory ? 200 : 160,
                    height: isStory ? 200 : 160,
                    background: "#FFFFFF",
                    padding: 10,
                    borderRadius: 16,
                    boxShadow: "0 12px 30px rgba(0,0,0,0.5)",
                  }}
                />
              )}
              {content.joinCode && (
                <div style={{ textAlign: "left" }}>
                  <div
                    style={{
                      fontFamily: theme.fontEyebrow,
                      fontSize: isStory ? 18 : 15,
                      color: theme.subtleTextColor,
                      letterSpacing: "0.3em",
                      textTransform: "uppercase",
                      fontWeight: 800,
                      marginBottom: 6,
                    }}
                  >
                    Join code
                  </div>
                  <div
                    style={{
                      fontFamily: theme.fontHeadline,
                      fontSize: isStory ? 64 : 48,
                      color: theme.accentColor,
                      letterSpacing: "0.15em",
                      lineHeight: 1,
                    }}
                  >
                    {content.joinCode}
                  </div>
                </div>
              )}
            </div>
          )}

          {content.smallPrint && (
            <div
              style={{
                marginTop: isStory ? 28 : 18,
                fontFamily: theme.fontBody,
                fontSize: isStory ? 18 : 15,
                color: theme.subtleTextColor,
                textAlign: "center",
                opacity: 0.85,
              }}
            >
              {content.smallPrint}
            </div>
          )}
        </div>
      </FbkCardShell>
    );
  },
);

export default LeagueLaunchCard;
